import { useMemo } from 'react';
import { filter, find } from 'lodash';
import { useSelector, useInstanceSelector } from '@client/hooks/redux.hooks';
import { pluralize } from '@sda/utils/string';
import { totalProductCapacity } from '@sda/utils/meta';
import {
  getShowTickets, getShowById, getProductsForShow, getProductsForShowNumTicketsPurchased,
} from '@client/selectors/entities/shows';
import { getCurrentSKU } from '@client/selectors/features/stripe';
import { isOwnerOrCollaborator } from '@client/selectors/helpers/session';
import { getDateTime } from '@sda/utils/date';
import { SHOW_END_HOUR_INTERVAL } from '@sda/constants';

export interface Badge {
  short: string;
  long: string;
}

export const TICKET_PURCHASE_FAIL_REASONS: Record<string, Badge> = {
  IS_CANCELLED: {
    short: 'CANCELLED',
    long: 'This show has been CANCELLED.',
  },
  SHOW_HAS_NOT_BEEN_ANNOUNCED: {
    short: 'Not yet available for purchase.',
    long: 'Tickets for this show will be available for purchase on...',
  },
  SOLD_OUT: {
    short: 'Sold Out',
    long: 'Tickets for this show are sold out.',
  },
  NO_LONGER_ON_SALE: {
    short: 'No longer on sale',
    long: 'Tickets for this show are no longer on sale.',
  },
  SHOW_ISSUE: {
    short: 'Not available for purchase',
    long: 'Tickets for this show are not available for purchase due to an issue with this show.',
  },
  TEMPORARILY_RESERVED: {
    short: 'Not available, Check back soon.',
    long: 'The final ticket(s) for this show are temporarily reserved for another customer in the process of completing their purchase. If they change their mind, the ticket(s) will become available again - check back in a few minutes!',
  },
  FEW_LEFT: {
    short: 'Only a few left!',
    long: 'Only a few left!',
  },
  CONFIG_ISSUE: {
    short: 'Not available for purchase.',
    long: 'Tickets for this show are not available for purchase due to a configuration issue with this show.',
  },
  UNABLE_TO_LOAD_PURCHASE_INFO: {
    short: 'We were not able to load purchase information.',
    long: 'We were not able to load purchase information for this show. Please refresh the page and try again.',
  },
  UNABLE_TO_LOAD_TICKET_INFORMATION: {
    short: 'We were not able to load ticket information.',
    long: 'We were not able to load ticket information for this show. Please refresh the page and try again.',
  },
  NOT_AVAILABLE: {
    short: 'Not available for purchase.',
    long: 'Tickets for this show are not available for purchase at this time.',
  },
};

// Preserve original short part of badges for matching
export const PASSES_PURCHASE_FAIL_REASONS: Record<string, Badge> = {
  IS_CANCELLED: {
    ...TICKET_PURCHASE_FAIL_REASONS.IS_CANCELLED,
    long: 'This series has been CANCELLED.',
  },
  SHOW_HAS_NOT_BEEN_ANNOUNCED: {
    ...TICKET_PURCHASE_FAIL_REASONS.SHOW_HAS_NOT_BEEN_ANNOUNCED,
    long: 'Passes for this series will be available for purchase on...',
  },
  SOLD_OUT: {
    ...TICKET_PURCHASE_FAIL_REASONS.SOLD_OUT,
    long: 'Passes for this series are sold out.',
  },
  NO_LONGER_ON_SALE: {
    ...TICKET_PURCHASE_FAIL_REASONS.NO_LONGER_ON_SALE,
    long: 'Passes for this series are no longer on sale.',
  },
  SHOW_ISSUE: {
    ...TICKET_PURCHASE_FAIL_REASONS.SHOW_ISSUE,
    long: 'Passes for this series are not available for purchase due to an issue with this series.',
  },
  TEMPORARILY_RESERVED: {
    ...TICKET_PURCHASE_FAIL_REASONS.TEMPORARILY_RESERVED,
    long: 'The final pass(es) for this series are temporarily reserved for another customer in the process of completing their purchase. If they change their mind, the pass(es) will become available again - check back in a few minutes!',
  },
  FEW_LEFT: {
    ...TICKET_PURCHASE_FAIL_REASONS.FEW_LEFT,
    long: 'Only a few left!',
  },
  CONFIG_ISSUE: {
    ...TICKET_PURCHASE_FAIL_REASONS.CONFIG_ISSUE,
    long: 'Passes for this series are not available for purchase due to a configuration issue with this series.',
  },
  UNABLE_TO_LOAD_PURCHASE_INFO: {
    ...TICKET_PURCHASE_FAIL_REASONS.UNABLE_TO_LOAD_PURCHASE_INFO,
    long: 'We were not able to load purchase information for this series. Please refresh the page and try again.',
  },
  UNABLE_TO_LOAD_TICKET_INFORMATION: {
    ...TICKET_PURCHASE_FAIL_REASONS.UNABLE_TO_LOAD_TICKET_INFORMATION,
    long: 'We were not able to load pass information for this series. Please refresh the page and try again.',
  },
  NOT_AVAILABLE: {
    ...TICKET_PURCHASE_FAIL_REASONS.NOT_AVAILABLE,
    long: 'Passes for this series are not available for purchase at this time.',
  },
};

interface UseTicketingCheckProps {
  readonly showId: string;
  readonly productId?: string;
}

export const useTicketingCheck = ({
  showId, productId = null,
}: UseTicketingCheckProps): [Badge, boolean, boolean, boolean] => {
  const allTickets = useSelector(getShowTickets);
  const products = useInstanceSelector(getProductsForShow, showId);
  let numTicketsPurchased = useInstanceSelector(getProductsForShowNumTicketsPurchased, showId);

  const product = find(products, (prod) => prod.id === productId);
  const tickets = productId ? filter(allTickets, (ticket) => ticket.stripeSKU === productId).length : allTickets.length;

  const show = useInstanceSelector(getShowById, showId);
  const currentSKU = useSelector(getCurrentSKU);

  const isCollaborator = useInstanceSelector(isOwnerOrCollaborator, show);

  if (productId) {
    numTicketsPurchased = product.numTicketsPurchased;
  }

  const {
    ticketEndTime, startTime, isCancelled, isShowGroup,
  } = show;

  const endTime = ticketEndTime ? getDateTime(ticketEndTime) : getDateTime(startTime).plus({ hours: SHOW_END_HOUR_INTERVAL });

  let { stripeSKU } = show;

  let capacity = totalProductCapacity(products);

  if (product) {
    capacity = product.capacity;
    stripeSKU = product.id;
  }

  const remainingTicketCount = useMemo(() => {
    // Since the listener fetches ten tickets at a time, the number of available tickets should always be set to 10
    // If this is less than 10, that means that either all but 9 tickets have been purchased, or some number of tickets has been reserved
    if (tickets < 10) {
      return tickets;
    }

    if (capacity === -1) {
      return tickets;
    }

    return capacity - numTicketsPurchased;
  }, [tickets, capacity, numTicketsPurchased]);

  const timeLimitPast = useMemo(() => getDateTime(endTime) < getDateTime(), [endTime]);

  const canPurchaseTickets = useMemo(
    () => {
      if (
        !stripeSKU
        || (timeLimitPast && !isCollaborator) // Allow showrunners to continue purchasing tickets after the show is over
        || !currentSKU
        || isCancelled
        || !currentSKU.enabled
        || !tickets
        || (capacity !== -1 && numTicketsPurchased && numTicketsPurchased >= capacity)
      ) {
        return false;
      }

      return true;
    },
    [
      capacity, currentSKU, isCollaborator,
      isCancelled, numTicketsPurchased, timeLimitPast, tickets,
      stripeSKU,
    ],
  );

  const badge = useMemo(
    () => {
      let badgeIndex: Badge = null;

      const FAIL_REASONS = isShowGroup ? PASSES_PURCHASE_FAIL_REASONS : TICKET_PURCHASE_FAIL_REASONS;

      if (isCancelled) {
        badgeIndex = FAIL_REASONS.IS_CANCELLED;
      } else if (numTicketsPurchased && numTicketsPurchased >= capacity && capacity !== -1) {
        badgeIndex = FAIL_REASONS.SOLD_OUT;
      } else if (currentSKU) {
        if (!canPurchaseTickets) {
          if (timeLimitPast || isCancelled) {
            badgeIndex = FAIL_REASONS.NO_LONGER_ON_SALE;
          } else if (!currentSKU.enabled) {
            badgeIndex = FAIL_REASONS.SHOW_ISSUE;
          } else {
            badgeIndex = FAIL_REASONS.TEMPORARILY_RESERVED;
          }
        } else if (tickets > 0 && numTicketsPurchased > capacity * 0.8 && capacity !== -1) {
          let returnBadgeIndex = FAIL_REASONS.FEW_LEFT;
          returnBadgeIndex.long = `Only ${pluralize('spot', remainingTicketCount)} left!`;
          badgeIndex = returnBadgeIndex;
        }
      } else if (!canPurchaseTickets) {
        if (!stripeSKU) {
          badgeIndex = FAIL_REASONS.CONFIG_ISSUE;
        } else if (!currentSKU) {
          badgeIndex = FAIL_REASONS.UNABLE_TO_LOAD_PURCHASE_INFO;
        } else if (!tickets) {
          badgeIndex = FAIL_REASONS.UNABLE_TO_LOAD_TICKET_INFORMATION;
        } else {
          badgeIndex = FAIL_REASONS.NOT_AVAILABLE;
        }
      }

      return badgeIndex;
    },
    [
      canPurchaseTickets, stripeSKU, currentSKU, tickets, capacity, numTicketsPurchased,
      remainingTicketCount, isCancelled, isShowGroup, timeLimitPast,
    ],
  );

  return [badge, canPurchaseTickets, timeLimitPast, isCollaborator];
};
